import React, { useState } from "react";
import { Form, FormGroup, Input, Label, Button, FormFeedback } from "reactstrap";
import { Link } from "react-router-dom";
import { Eye, EyeOff } from "react-feather";
import classNames from "classnames";
import { useCookies } from 'react-cookie';

import Logo from "~/assets/images/login/login-logo.svg";

import ValidationGroup, { ValidatorMark } from "~/components/validation";

import getRecaptcha from "../_services/recaptcha";
import security     from "~/services/security";


const Register = (props)=> {

  const defaultAccount = {
    firstName       : "",
    lastName        : "",
    email           : "",
    password        : "",
    confirmPassword : "",
    agree           : false,
  };

  const [account               , setAccount]               = useState(defaultAccount);
  const [loading               , setLoading]               = useState(false);

  const [togglePassword        , setTogglePassword]        = useState(false);
  const [toggleConfirmPassword , setToggleConfirmPassword] = useState(false);

  const [errors                , setErrors]                = useState({});
  const [cookies               , _setCookie, removeCookie] = useCookies();


  const validation = ValidationGroup("Register");

  const changeProp = (prop, value)=> setAccount({...account, [prop]: value});

  const hasErrors = ()=> {

    const errorList = {
      ...validation("agree"          , account.agree),
      ...validation("firstName"      , account.firstName),
      ...validation("lastName"       , account.lastName),
      ...validation("email"          , account.email),
      ...validation("password"       , account.password       , {match: account.confirmPassword}),
      ...validation("confirmPassword", account.confirmPassword, {match: account.password})
    };

    setErrors(errorList);
    return Boolean(Object.keys(errorList).length);
  };

  const submitSignUp = async (e)=> {

    e.preventDefault();
    if(hasErrors()) return;
    setLoading(true);

    const recaptcha = await getRecaptcha();
    const credentials = {
      ...{firstName: account.firstName, lastName: account.lastName, email: account.email, password: account.password, referralCode : cookies.referral, recaptcha },
      ...(cookies.postbackTracking ? {postbackTracking: cookies.postbackTracking} : {})
    };

    return security.register(credentials).then((message)=> {
        removeCookie("postbackTracking", {
          domain   : process.env.REACT_APP_COOKIE_DOMAIN,
          path     : "/",
          sameSite : 'lax',
          secure   : true,
        });
        return props.history.push({pathname: "/login", message: {text: message, type: "success"}});
      })
      .catch((err) => {
        if(err.status === 401 && "banMessage" in err.data.error) 
          return props.history.push({pathname: "/login", message: {banMessage: err.data.error.banMessage, type: "error"}});
        setErrors(err);
      })
      .finally(()=> setLoading(false));
  };

  return (
    <div className="login-card">
      <div style={{marginTop: 100}}>

        <Link to={"/login"} className="logo"> <img src={Logo}/> </Link>

        <div className="login-main">
          <Form className="theme-form text-center" onSubmit={submitSignUp}>

            <h3 className='greeting'> Create your account </h3>
            { errors.generalError && <p className='text-danger'>{ errors.generalError } </p> }

            <FormGroup>
              <Label className="col-form-label">First Name</Label>
              <Input type="text"
                value={account.firstName}
                onChange={(e)=> changeProp("firstName", e.target.value)}
                className={classNames({ valid: !validation("firstName", account.firstName), 'is-invalid': errors.firstName})}
                placeholder="First name"
              />
              <ValidatorMark val={account.firstName} name="firstName" validation={validation} errors={errors} />
            </FormGroup>

            <FormGroup>
              <Label className="col-form-label">Last Name</Label>
              <Input type="text"
                value={account.lastName}
                onChange={(e)=> changeProp("lastName", e.target.value)}
                className={classNames({ valid: !validation("lastName", account.lastName), 'is-invalid': errors.lastName})}
                placeholder="Last name"
              />
              <ValidatorMark val={account.lastName} name="lastName" validation={validation} errors={errors} />
            </FormGroup>

            <FormGroup>
              <Label className="col-form-label">Email Address</Label>
              <Input type="text"
                value={account.email}
                onChange={(e)=> changeProp("email", e.target.value)}
                className={classNames({ valid: !validation("email", account.email), 'is-invalid': errors.email})}
                placeholder="Email"
              />
              <ValidatorMark val={account.email} name="email" validation={validation} errors={errors} />
            </FormGroup>

            <FormGroup>
              <Label className="col-form-label">Password</Label>
              <Input type={togglePassword ? "text" : "password"}
                value={account.password}
                onChange={(e)=> changeProp("password", e.target.value)}
                className={classNames({'is-invalid': errors.password})}
                placeholder="Password"
              />
              <ValidatorMark val={account.password} name="password" validation={validation} errors={errors} textOnly={true} />
              <div className="validation-mark" onClick={()=> setTogglePassword(!togglePassword)} >
                { togglePassword ? <Eye color="var(--theme-deafult)"/> : <EyeOff  color="var(--theme-deafult)" /> }
              </div>
            </FormGroup>

            <FormGroup>
              <Label className="col-form-label">Confirm Password</Label>
              <Input type={toggleConfirmPassword ? "text" : "password"}
                value={account.confirmPassword}
                onChange={(e)=> changeProp("confirmPassword", e.target.value)}
                className={classNames({'is-invalid': errors.confirmPassword})}
                placeholder="Confirm Password"
              />
              <ValidatorMark val={account.confirmPassword} name="confirmPassword" validation={validation} errors={errors} textOnly={true} />

              <div className="validation-mark" onClick={()=> setToggleConfirmPassword(!toggleConfirmPassword) } >
                { toggleConfirmPassword ? <Eye color="var(--theme-deafult)"/> : <EyeOff  color="var(--theme-deafult)" /> }
              </div>
            </FormGroup>

            <FormGroup className="mb-3 mt-4 text-center">
              <Input
                id="terms"
                type='checkbox'
                checked={account.agree}
                onChange={()=> changeProp("agree", !account.agree) }
                className={`${errors.agree ? 'is-invalid' : ''}`}
              />
              <Label for="terms">
                <small className="text-muted">
                  Agree to our
                  <a className="ml-1" href="https://earndorado.com/privacy" target="_blank"> Privacy Policy </a>
                  and <a className="ml-1" href="https://earndorado.com/terms" target="_blank"> Terms & Conditions </a>
                </small>
              </Label>

              { errors.agree && <FormFeedback> { errors.agree }  </FormFeedback>}
            </FormGroup>

            <FormGroup className="m-t-50">
              <Button disabled={loading} color="login" className="btn-block" type="submit"> Create account </Button>
            </FormGroup>

            <p> Have an account? <Link className="ml-2 mr-2" to="/login"> Sign In </Link> </p>

          </Form>
        </div>
      </div>
    </div>
  );
};

export default Register;
